import { Row, Col, Table, Spin, Form, DatePicker, Modal, message } from "antd";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useFetch } from "hooks/useFetch";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { CALL_FEATCH_API } from "common/API";
import { ExclamationCircleFilled, CloseOutlined } from "@ant-design/icons";
import { currencySymbolMap } from "common/Constants";
import { ReactComponent as FilterIcon } from "../../../assets/filter-svgrepo-com (2).svg";
import { ReactComponent as Editicon } from "../../../assets/Edit_Pencil_01.svg";
import { ReactComponent as TrashDelete } from "../../../assets/Trash_Full.svg";
import moment from "moment";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";

const FilterBtn = styled.div`
  position: relative;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: #deefee;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;
  transition: border-color 0.2s;
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #fff;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 680px;
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 14px;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px 0;
`;

const DetailBody = styled.div`
  padding: 0 24px 24px;
  overflow-y: auto;
  flex: 1;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
`;

const DetailLabel = styled.span`
  flex: 0 0 50%;
  color: #888;
  font-size: 14px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #222;
  flex: 1;
  word-break: break-word;
  white-space: pre-wrap;
`;

const CreatedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 20px;
  padding: 3px 12px;
  font-weight: 500;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const STATUS = {
  WAITING: "waiting",
  APPROVED: "approved",
  DECLINED: "declined",
  PAID: "paid",
};

const DEFAULT_STATUS = ["Waiting for Approval"];

function normalizeStatus(record) {
  const raw = (record.status || record.statusType || "").toLowerCase().trim();
  if (raw.includes("waiting") || raw.includes("approval")) return STATUS.WAITING;
  if (raw.includes("paid")) return STATUS.PAID;
  if (raw.includes("approved")) return STATUS.APPROVED;
  if (raw.includes("declined") || raw.includes("rejected")) return STATUS.DECLINED;
  return raw;
}

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day}${suffix} ${month} ${year}`;
};

function resolveNames(emailsOrIds, userList, currentUserId = "") {
  if (!Array.isArray(emailsOrIds) || !emailsOrIds.length) return "---";
  return emailsOrIds
    .map((val) => {
      if (!val) return "—";
      if (
        currentUserId &&
        (val === currentUserId ||
          val?.toLowerCase() === currentUserId?.toLowerCase())
      )
        return "You";
      const user = userList?.find(
        (u) => u?.email === val || u?.userid === val || u?.emailid === val
      );
      return user?.fullname || user?.firstname || val;
    })
    .join(", ");
}

function FundRequestDetailModal({
  record,
  onClose,
  onMarkAsPaid,
  onSend,
  onDelete,
  onEdit,
  userEmail = "",
  onApprove,
  onDecline,
  showActions = false,
}) {
  const userList = useSelector((store) => get(store, "common.userList", []));
  if (!record) return null;

  const isPaid = record?.paidstatus === 1;
  const status = normalizeStatus(record);

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <ModalTitle>
              Fund Request{" "}
              <span style={{ fontWeight: 400, color: "#555" }}>
                ({record.fundid || record.id})
              </span>
            </ModalTitle>

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <CreatedBadge style={{ marginBottom: 0 }}>
                <b>Created by:</b>{" "}
                {(() => {
                  const name = resolveNames([record.createdby], userList, userEmail);
                  const date =
                    record.createddate || record.createdat
                      ? moment(record.createdat || record.createddate).format("Do MMM, YYYY")
                      : "";
                  return `${name}${date ? "; " + date : ""}`;
                })()}
              </CreatedBadge>

              {status === STATUS.APPROVED && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#e6f9f0",
                    color: "#1a4731",
                    fontSize: 12,
                    padding: "3px 12px",
                    borderRadius: 20,
                  }}
                >
                  <span style={{ color: "#888", fontWeight: 400 }}>Approved by</span>
                  <span style={{ fontWeight: 500 }}>
                    {resolveNames([record.approvedby], userList, userEmail)}
                    {(record.approvedat || record.approveddate) &&
                      `; ${formatDate(record.approvedat || record.approveddate)}`}
                  </span>
                </span>
              )}

              {status === STATUS.DECLINED && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fde8e8",
                    color: "#7f1d1d",
                    fontSize: 12,
                    padding: "3px 12px",
                    borderRadius: 20,
                  }}
                >
                  <span style={{ color: "#888", fontWeight: 400 }}>Declined by</span>
                  <span style={{ fontWeight: 500 }}>
                    {resolveNames([record.declinedby], userList, userEmail)}
                    {(record.declinedat || record.declineddate) &&
                      `; ${formatDate(record.declinedat || record.declineddate)}`}
                  </span>
                </span>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {status === STATUS.WAITING && onEdit && (
              <>
                <Editicon
                  onClick={(e) => { e.stopPropagation(); onClose(); onEdit(record); }}
                  style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
                />
                <TrashDelete
                  onClick={(e) => { e.stopPropagation(); onDelete(record); onClose(); }}
                  style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
                />
              </>
            )}
            {status === STATUS.APPROVED && !isPaid && onSend && (
              <>
                <TrashDelete
                  onClick={(e) => { e.stopPropagation(); onDelete(record); onClose(); }}
                  style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
                />
                <ButtonFilled onClick={(e) => { e.stopPropagation(); onClose(); onSend(record); }}>
                  Send
                </ButtonFilled>
                <ButtonFilled onClick={(e) => { e.stopPropagation(); onClose(); onMarkAsPaid(record); }}>
                  Mark as Paid
                </ButtonFilled>
              </>
            )}
            {status === STATUS.DECLINED && onDelete && (
              <TrashDelete
                onClick={(e) => { e.stopPropagation(); onDelete(record); onClose(); }}
                style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
              />
            )}
            <CloseBtn onClick={onClose}>
              <CloseOutlined />
            </CloseBtn>
          </div>
        </ModalHeader>

        <DetailBody>
          {[
            ["Client", record.clientname || record.client],
            ["Request Type", record.requestfundtype || record.requesttype],
            [
              "Amount",
              (() => {
                const amount = record.requestamount ?? record.amount ?? "–";
                const symbol = currencySymbolMap[record.currency] ?? record.currency ?? "$";
                return amount !== "–" ? `${symbol} ${amount}` : "–";
              })(),
            ],
            ["Due Date", formatDate(record.duedate)],
            ["Status", record.status?.trim() || "Waiting for Approval"],
            ["Matter", record.matter === " " ? "–" : record.matter],
          ].map(([label, value]) => (
            <DetailRow key={label}>
              <DetailLabel>{label}</DetailLabel>
              <DetailValue>{value || "–"}</DetailValue>
            </DetailRow>
          ))}

          <div style={{ padding: "12px 0" }}>
            <div style={{ color: "#888", fontSize: 14, marginBottom: 6 }}>Description</div>
            <div style={{ fontSize: 14, color: "#222", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
              {record.description || "–"}
            </div>
          </div>

          {record.declinedreason?.trim() && (
            <div style={{ padding: "12px 0", borderTop: "1px solid #f3f4f6" }}>
              <div style={{ color: "#888", fontSize: 14, marginBottom: 6 }}>Declined Reason</div>
              <div style={{ fontSize: 14, color: "#222", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                {record.declinedreason}
              </div>
            </div>
          )}

          {showActions && status === STATUS.WAITING && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
              <ButtonOutlined onClick={() => onDecline && onDecline(record)}>Decline</ButtonOutlined>
              <ButtonFilled onClick={() => onApprove && onApprove(record)}>Approve</ButtonFilled>
            </div>
          )}
        </DetailBody>
      </ModalBox>
    </Overlay>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */

const FundRequest = () => {
  const [form] = Form.useForm();
  const [hoveredRow, setHoveredRow] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterForm] = Form.useForm();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeFilterCount, setActiveFilterCount] = useState(1); // 1 for default status
  const userInfo = useSelector((store) => get(store, "common.userInfo"));
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineLoading, setDeclineLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    matters: [],
    requestTypes: [],
    statuses: [],
  });
  const [filterPage, setFilterPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterTotal, setFilterTotal] = useState(0);
  const [dateFilterCount, setDateFilterCount] = useState(0);

  const loginUserId = userInfo?.userid || userInfo?.email || "";
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: fundRequestRes, isLoading, refetch } = useFetch(
    ["getFundRequest", currentPage],
    `insyts-legal/getfundrequest?loginuserid=${encodeURIComponent(loginUserId)}&page=${currentPage}&limit=${pageSize}`
  );

  const fundRequestData = fundRequestRes?.response?.data || [];
  const totalRecords = fundRequestRes?.response?.pagination?.total || 0;

  // ── Set default status filter on mount once loginUserId is ready ──
  useEffect(() => {
    if (!loginUserId) return;
    form.setFieldsValue({ status: DEFAULT_STATUS });
    callFilterApi({ status: DEFAULT_STATUS }, 1);
  }, [loginUserId]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await CALL_FEATCH_API(
          `insyts-legal/getfundrequestfilterlist?loginuserid=${encodeURIComponent(loginUserId)}`,
          "get"
        );
        const data = res?.response?.data || {};
        setFilterOptions({
          clients: (data.clients || [])
            .filter((c) => c.clientname?.trim())
            .map((c) => ({ label: String(c.clientname).trim(), value: String(c.clientname).trim() })),
          matters: (data.matters || [])
            .filter((m) => m.mattername?.trim())
            .map((m) => ({ label: String(m.mattername).trim(), value: String(m.mattername).trim() })),
          requestTypes: (data.requesttypes || [])
            .filter(Boolean)
            .map((rt) => ({ label: String(rt), value: String(rt) })),
          statuses: [
            ...new Set([
              "Waiting for Approval",
              ...(data.statuses || []).filter(Boolean).map((s) => String(s).trim()),
            ]),
          ].map((s) => ({ label: s, value: s })),
        });
      } catch (err) {
        console.error("Failed to fetch filter options", err);
      }
    };
    if (loginUserId) fetchFilterOptions();
  }, [loginUserId]);

  const callFilterApi = async (overrideValues = {}, page = 1) => {
    const headerValues = form.getFieldsValue();
    const modalValues = filterForm.getFieldsValue();
    const merged = { ...modalValues, ...headerValues, ...overrideValues };

    const createdfrom = merged?.logDateFrom
      ? moment(merged.logDateFrom).format("YYYY-MM-DD")
      : "";
    const createdto = merged?.logDateTo
      ? moment(merged.logDateTo).format("YYYY-MM-DD")
      : "";
    const duefrom = merged?.createdDateFrom
      ? moment(merged.createdDateFrom).format("YYYY-MM-DD")
      : "";
    const dueto = merged?.createdDateTo
      ? moment(merged.createdDateTo).format("YYYY-MM-DD")
      : "";

    const rawParams = {
      loginuserid: loginUserId,
      page,
      limit: pageSize,
      ...(createdfrom && { createdfrom }),
      ...(createdto && { createdto }),
      ...(duefrom && { duefrom }),
      ...(dueto && { dueto }),
      ...(merged.client?.length && { client: merged.client.join(",") }),
      ...(merged.requestType?.length && { requesttype: merged.requestType.join(",") }),
      ...(merged.matter?.length && { matter: merged.matter.join(",") }),
      ...(merged.status?.length && { status: merged.status.join(",") }),
    };

    const query = new URLSearchParams(rawParams).toString();
    try {
      const res = await CALL_FEATCH_API(`insyts-legal/getfundrequestfilter?${query}`, "get");
      const result = res?.response?.data || res?.data || [];
      const total = res?.response?.pagination?.total || 0;
      setFilteredData(Array.isArray(result) ? result : []);
      setFilterTotal(total);
      setIsFiltering(true);
      setFilterPage(page);
    } catch (err) {
      console.error("Filter API error", err);
    }
  };

  const applyDateFilters = async () => {
    const values = filterForm.getFieldsValue();
    const createdrange = values?.createdrange;
    const duerange = values?.duerange;

    const hasCreated = Array.isArray(createdrange) && createdrange[0] && createdrange[1];
    const hasDue = Array.isArray(duerange) && duerange[0] && duerange[1];

    let count = 0;
    if (hasCreated) count++;
    if (hasDue) count++;
    setDateFilterCount(count);

    await callFilterApi({
      logDateFrom: hasCreated ? createdrange[0] : null,
      logDateTo: hasCreated ? createdrange[1] : null,
      createdDateFrom: hasDue ? duerange[0] : null,
      createdDateTo: hasDue ? duerange[1] : null,
    }, 1);

    setFilterModalOpen(false);
  };

  const clearDateFilters = () => {
    filterForm.resetFields();
    setDateFilterCount(0);
    setFilterModalOpen(false);
    const headerValues = form.getFieldsValue();
    callFilterApi({ ...headerValues }, 1);
  };

  const displayData = (filteredData !== null ? filteredData : fundRequestData)
    .slice()
    .sort((a, b) =>
      new Date(b.createdat || b.createddate || 0) -
      new Date(a.createdat || a.createddate || 0)
    );

  const fmt = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return v;
    return n % 1 === 0 ? String(Math.trunc(n)) : n.toString();
  };

  const handleHeaderFilterChange = (values) => {
    const client = values?.client || [];
    const requestType = values?.requestType || [];
    const matter = values?.matter || [];
    const status = values?.status || [];

    const count = client.length + requestType.length + matter.length + status.length;
    setActiveFilterCount(count);

    callFilterApi({ client, requestType, matter, status }, 1);
  };

  const handleFundAction = async (actionStatus) => {
    if (actionStatus === "Declined") {
      setIsModalOpen(false);
      setTimeout(() => setIsDeclineModalOpen(true), 200);
      return;
    }

    const payload = {
      fundid: selectedRecord?.fundid || selectedRecord?.id,
      status: actionStatus,
      approvedby: userInfo?.userid,
      updatedby: userInfo?.userid,
    };

    try {
      const res = await CALL_FEATCH_API(`insyts-legal/updatefundrequest`, "PATCH", payload);
      const isSuccess =
        res?.status === "SUCCESS" ||
        res?.status === 200 ||
        res?.message?.toLowerCase?.()?.includes("success");

      if (isSuccess) {
        message.success("Approved successfully");
        setIsModalOpen(false);
        setSelectedRecord(null);
        setFilteredData(null);
        refetch();
      } else {
        console.error("Action failed:", res);
      }
    } catch (err) {
      console.error("Fund action failed", err);
    }
  };

  const handleDeclineSubmit = async () => {
    if (!declineReason.trim()) return;

    const payload = {
      fundid: selectedRecord?.fundid || selectedRecord?.id,
      status: "Declined",
      declinedby: userInfo?.userid,
      declinedreason: declineReason.trim(),
      updatedby: userInfo?.userid,
    };

    try {
      setDeclineLoading(true);
      const res = await CALL_FEATCH_API(`insyts-legal/updatefundrequest`, "PATCH", payload);
      const isSuccess =
        res?.status === "SUCCESS" ||
        res?.status === 200 ||
        res?.message?.toLowerCase?.()?.includes("success");

      if (isSuccess) {
        message.success("Declined successfully");
        setIsDeclineModalOpen(false);
        setSelectedRecord(null);
        setDeclineReason("");
        setFilteredData(null);
        refetch();
      } else {
        console.error("Decline failed:", res);
      }
    } catch (err) {
      console.error("Decline error:", err);
    } finally {
      setDeclineLoading(false);
    }
  };

  function isOverdue(record) {
    const status = (record.status || "").toLowerCase();
    if (!status.includes("waiting")) return false;
    const due = record.duedate || record.dueDate;
    if (!due) return false;
    return new Date(due) < new Date();
  }

  const columns = [
    { title: "Client", dataIndex: "client" },
    {
      title: "ID",
      dataIndex: "fundid",
      render: (_, r) => r.fundid || r.id || "–",
    },
    { title: "Request Type", dataIndex: "requesttype" },
    {
      title: "Matter",
      dataIndex: "matter",
      render: (_, r) => {
        const name = r.mattername || r.matter;
        const id = r.matterid;
        if (!name && !id) return "–";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {name && (
              <span
                style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={name}
              >
                {name}
              </span>
            )}
            {id && <span style={{ fontSize: 11, color: "#9ca3af" }}>{id}</span>}
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, r) => {
        const amount = r.requestamount || r.amount || "–";
        const symbol = currencySymbolMap[r.currency] ?? r.currency ?? "$";
        return `${symbol} ${fmt(amount)}`;
      },
    },
    {
      title: "Due Date",
      dataIndex: "duedate",
      render: (_, record) => {
        const overdue = isOverdue(record);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {overdue && <ExclamationCircleFilled style={{ color: "#ef4444", fontSize: 14 }} />}
            <span style={{ fontStyle: overdue ? "italic" : "normal" }}>
              {formatDate(record.duedate)}
            </span>
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, r) => r.status?.trim() || "Waiting for Approval",
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>
            Total Log: {isFiltering ? filterTotal : totalRecords}
          </span>
        </Col>

        <Col>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Form
              form={form}
              onValuesChange={(_, allValues) => handleHeaderFilterChange(allValues)}
              layout="inline"
              style={{ gap: 0 }}
            >
              <Form.Item name="client" style={{ margin: "0 8px 0 0" }}>
                <DropdownCheckBox
                  name="client"
                  label="Client"
                  options={filterOptions.clients}
                  form={form}
                  handleFormChange={handleHeaderFilterChange}
                />
              </Form.Item>

              <Form.Item name="requestType" style={{ margin: "0 8px 0 0" }}>
                <DropdownCheckBox
                  name="requestType"
                  label="Request Type"
                  options={filterOptions.requestTypes}
                  form={form}
                  handleFormChange={handleHeaderFilterChange}
                />
              </Form.Item>

              <Form.Item name="matter" style={{ margin: "0 8px 0 0" }}>
                <DropdownCheckBox
                  name="matter"
                  label="Matter"
                  options={filterOptions.matters}
                  form={form}
                  handleFormChange={handleHeaderFilterChange}
                />
              </Form.Item>

              <Form.Item name="status" style={{ margin: 0 }}>
                <DropdownCheckBox
                  name="status"
                  label="Status"
                  options={filterOptions.statuses}
                  form={form}
                  handleFormChange={handleHeaderFilterChange}
                />
              </Form.Item>
            </Form>

            <FilterBtn onClick={() => setFilterModalOpen(true)} title="Advanced Filters">
              <FilterIcon style={{ cursor: "pointer", backgroundColor: "#C2FAE5", fontSize: 20 }} />
              {dateFilterCount > 0 && (
                <FilterBadge>{dateFilterCount}</FilterBadge>
              )}
            </FilterBtn>
          </div>
        </Col>
      </Row>

      <Spin spinning={isLoading}>
        <Table
          columns={columns}
          dataSource={displayData}
          rowKey={(r) => r.fundid || r.id}
          pagination={{
            current: isFiltering ? filterPage : currentPage,
            pageSize: pageSize,
            total: isFiltering ? filterTotal : totalRecords,
            onChange: (page) => {
              if (isFiltering) {
                const headerValues = form.getFieldsValue();
                const modalValues = filterForm.getFieldsValue();
                callFilterApi({ ...modalValues, ...headerValues }, page);
              } else {
                setCurrentPage(page);
                setFilteredData(null);
              }
            },
            showSizeChanger: false,
            style: { marginTop: 16, textAlign: "right" },
          }}
          onRow={(record) => ({
            onClick: () => { setSelectedRecord(record); setIsModalOpen(true); },
            onMouseEnter: () => setHoveredRow(record?.id),
            onMouseLeave: () => setHoveredRow(null),
            style: { cursor: "pointer" },
          })}
        />
      </Spin>

      {/* ── FILTER MODAL ── */}
      <Modal
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        footer={null}
        width={900}
        title={`Filter${dateFilterCount > 0 ? ` (${dateFilterCount})` : ""}`}
        centered
      >
        <Form
          form={filterForm}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            const createdrange = allValues?.createdrange;
            const duerange = allValues?.duerange;

            const hasCreated = Array.isArray(createdrange) && createdrange?.[0] && createdrange?.[1];
            const hasDue = Array.isArray(duerange) && duerange?.[0] && duerange?.[1];

            const count = (hasCreated ? 1 : 0) + (hasDue ? 1 : 0);
            setDateFilterCount(count);

            if (!hasCreated && !hasDue) {
              setFilteredData(null);
              setIsFiltering(false);
              setFilterTotal(0);
              setFilterPage(1);
              return;
            }

            callFilterApi({
              logDateFrom: hasCreated ? createdrange[0] : null,
              logDateTo: hasCreated ? createdrange[1] : null,
              createdDateFrom: hasDue ? duerange[0] : null,
              createdDateTo: hasDue ? duerange[1] : null,
            }, 1);
          }}
        >
          <div style={{ paddingTop: 8 }}>
            <div style={{ display: "flex", gap: 24, marginBottom: 28, flexWrap: "wrap" }}>

              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                  Created Date Range
                </div>
                <Form.Item name="createdrange" style={{ margin: 0 }}>
                  <DatePicker.RangePicker
                    style={{ width: "100%", borderRadius: 20, height: 40 }}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </div>

              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                  Due Date Range
                </div>
                <Form.Item name="duerange" style={{ margin: 0 }}>
                  <DatePicker.RangePicker
                    style={{ width: "100%", borderRadius: 20, height: 40 }}
                    format="DD-MM-YYYY"
                  />
                </Form.Item>
              </div>

            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f3f4f6", paddingTop: 16 }}>
              <ButtonOutlined onClick={clearDateFilters}>Clear All</ButtonOutlined>
              <ButtonFilled onClick={applyDateFilters}>Apply Filter</ButtonFilled>
            </div>
          </div>
        </Form>
      </Modal>

      {/* ── VIEW MODAL ── */}
      {isModalOpen && selectedRecord && (
        <FundRequestDetailModal
          record={selectedRecord}
          userEmail={loginUserId}
          onClose={() => { setIsModalOpen(false); setSelectedRecord(null); }}
          showActions
          onApprove={() => handleFundAction("Approved")}
          onDecline={() => handleFundAction("Declined")}
          onDelete={null}
          onEdit={null}
          onSend={null}
          onMarkAsPaid={null}
        />
      )}

      {/* ── DECLINE REASON MODAL ── */}
      <Modal
        open={isDeclineModalOpen}
        onCancel={() => {
          setIsDeclineModalOpen(false);
          setDeclineReason("");
          setIsModalOpen(true);
        }}
        footer={null}
        width={420}
        title={
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
            Reason for decline
          </span>
        }
      >
        <div style={{ paddingTop: 8 }}>
          <textarea
            placeholder="Type something.."
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            style={{
              width: "100%",
              minHeight: 120,
              padding: "10px 14px",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              resize: "vertical",
              outline: "none",
              fontSize: 14,
              color: "#374151",
              boxSizing: "border-box",
              fontFamily: "inherit",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <ButtonFilled
              onClick={handleDeclineSubmit}
              disabled={!declineReason.trim() || declineLoading}
            >
              {declineLoading ? "Declining..." : "Decline"}
            </ButtonFilled>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FundRequest;